import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button, TextInput, Textarea } from "@mantine/core";
import apiService from "../../services/api";
import type { SessionRequest } from "../../types";

interface TaskObject {
  description?: string;
  completed?: boolean;
  [key: string]: unknown;
}

interface SessionRequestDetailsResponse {
  Code?: number;
  Message?: string;
  request?: SessionRequest;
  session?: {
    session_summary?: string;
    training_tasks?: string | TaskObject[] | unknown;
    notes?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface UpdateSessionResponse {
  Code?: number;
  Message?: string;
  [key: string]: unknown;
}

const PostSessionForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestId = searchParams.get("request_id");

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const [sessionData, setSessionData] = useState<SessionRequest | null>(null);
  const [_aiSummary, setAiSummary] = useState<string>("");
  const [editedSummary, setEditedSummary] = useState<string>("");
  const [trainingTasks, setTrainingTasks] = useState<string[]>([""]);
  const [sessionNotes, setSessionNotes] = useState<string>("");

  const normalizeTasksForForm = (rawTasks: unknown): string[] => {
    if (!rawTasks) return [];
    let parsed: unknown = rawTasks;
    if (typeof rawTasks === "string") {
      try {
        parsed = JSON.parse(rawTasks);
      } catch (err) {
        console.log("Failed to parse as JSON array:", err);
        parsed = [rawTasks];
      }
    }
    if (!Array.isArray(parsed)) {
      parsed = [parsed];
    }
    return (parsed as unknown[])
      .map((task: unknown) => {
        if (typeof task === "string") return task;
        if (task && typeof task === "object") {
          return (task as TaskObject).description || "";
        }
        return "";
      })
      .filter(Boolean) as string[];
  };

  useEffect(() => {
    if (!requestId) {
      setError("No session request ID provided");
      setLoading(false);
      return;
    }

    loadSessionData();
  }, [requestId]);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      const response = (await apiService.getSessionRequestDetails(
        requestId as string,
      )) as SessionRequestDetailsResponse;

      if (response.Code === 200) {
        setSessionData(response.request as SessionRequest);

        if (response.session) {
          const existingSummary = response.session.session_summary || "";
          setAiSummary(existingSummary);
          setEditedSummary(existingSummary);

          const formTasks = normalizeTasksForForm(response.session.training_tasks);
          setTrainingTasks(formTasks.length > 0 ? formTasks : [""]);
          setSessionNotes(response.session.notes || "");
        } else {
          setAiSummary("");
          setEditedSummary("");
          setTrainingTasks([""]);
          setSessionNotes("");
        }
      } else {
        setError(response.Message || "Failed to load session data");
      }
    } catch (err: unknown) {
      console.error("Error loading session data:", err);
      setError("Failed to load session data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = () => {
    setTrainingTasks([...trainingTasks, ""]);
  };

  const handleRemoveTask = (index: number) => {
    if (trainingTasks.length > 1) {
      const newTasks = trainingTasks.filter((_, i) => i !== index);
      setTrainingTasks(newTasks);
    }
  };

  const handleTaskChange = (index: number, value: string) => {
    const newTasks = [...trainingTasks];
    newTasks[index] = value;
    setTrainingTasks(newTasks);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate - at least one task must be filled
    const validTasks = trainingTasks
      .map((task) => (task || "").trim())
      .filter((task) => task.length > 0);
    if (validTasks.length === 0) {
      setError("Please add at least one training task for the client");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const formattedTasks = validTasks.map((task) => ({
        description: task,
        completed: false,
      }));

      const updateData = {
        session_summary: editedSummary,
        training_tasks: formattedTasks,
        notes: sessionNotes,
      };

      const response = (await apiService.updateSessionFromRequest(
        requestId as string,
        updateData,
      )) as UpdateSessionResponse;

      if (response.Code === 200) {
        setSuccess(true);
        // Redirect to trainer portal after 2 seconds
        setTimeout(() => {
          navigate("/trainer");
        }, 2000);
      } else {
        setError(response.Message || "Failed to save session artifacts");
      }
    } catch (err: unknown) {
      console.error("Error saving session artifacts:", err);
      setError("Failed to save session artifacts: " + (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="font-sans bg-background min-h-screen p-5">
        <div className="max-w-[900px] mx-auto bg-card rounded-2xl p-5 sm:p-8 md:p-10 shadow-[0_20px_60px_rgba(230,126,34,0.15)] border border-border text-foreground">
          <div className="text-center p-10 text-muted-foreground">Loading session data...</div>
        </div>
      </div>
    );
  }

  if (error && !sessionData) {
    return (
      <div className="font-sans bg-background min-h-screen p-5">
        <div className="max-w-[900px] mx-auto bg-card rounded-2xl p-5 sm:p-8 md:p-10 shadow-[0_20px_60px_rgba(230,126,34,0.15)] border border-border text-foreground">
          <div className="bg-destructive/10 border-2 border-destructive/25 text-destructive p-4 rounded-lg mb-5">
            {error}
          </div>
          <Button onClick={() => navigate("/trainer")} radius="md" size="md">
            Back to Trainer Portal
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="font-sans bg-background min-h-screen p-5">
        <div className="max-w-[900px] mx-auto bg-card rounded-2xl p-5 sm:p-8 md:p-10 shadow-[0_20px_60px_rgba(230,126,34,0.15)] border border-border text-foreground">
          <div className="bg-success/10 border-2 border-success/30 text-success p-4 rounded-lg mb-5 text-center text-lg font-semibold">
            Session artifacts saved successfully! Redirecting to trainer portal...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans bg-background min-h-screen p-5">
      <div className="max-w-[900px] mx-auto bg-card rounded-2xl p-5 sm:p-8 md:p-10 shadow-[0_20px_60px_rgba(230,126,34,0.15)] border border-border text-foreground">
        <div className="mb-8 border-b-2 border-border pb-5">
          <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2">
            Post-Session Artifacts
          </h1>
          <p className="text-base text-muted-foreground">
            Complete the session documentation for your client
          </p>
        </div>

        {error && (
          <div className="bg-destructive/10 border-2 border-destructive/25 text-destructive p-4 rounded-lg mb-5">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Session Summary Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2.5">
              Session Summary
            </h2>
            <p className="text-sm text-muted-foreground italic mt-1 mb-3">
              Optional — you can write this now or come back to it later from your Session History.
            </p>
            <Textarea
              value={editedSummary}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setEditedSummary(e.target.value)
              }
              placeholder="Summarize what was covered in this session…"
              autosize
              minRows={10}
              maxRows={20}
              radius="md"
              size="md"
            />
          </div>

          {/* Training Tasks Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2.5">
              Training Tasks for Client
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Add specific tasks or exercises for the client to work on before the next session
            </p>

            {trainingTasks.map((task, index) => (
              <div key={index} className="flex gap-2.5 mb-2.5 items-start">
                <TextInput
                  value={task}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleTaskChange(index, e.target.value)
                  }
                  placeholder={`Task ${index + 1} (e.g., "Practice sit command 10 times daily")`}
                  radius="md"
                  size="md"
                  className="flex-1"
                />
                {trainingTasks.length > 1 && (
                  <Button
                    type="button"
                    onClick={() => handleRemoveTask(index)}
                    color="red"
                    radius="md"
                    size="sm"
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}

            <Button
              type="button"
              onClick={handleAddTask}
              color="green"
              radius="md"
              size="sm"
              className="mt-2.5"
            >
              + Add Another Task
            </Button>
          </div>

          {/* Additional Notes Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2.5">
              Additional Notes (Optional)
            </h2>
            <Textarea
              value={sessionNotes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setSessionNotes(e.target.value)
              }
              placeholder="Any additional notes or observations..."
              minRows={4}
              radius="md"
              size="md"
            />
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <Button type="submit" loading={saving} radius="md" size="lg" className="mt-5">
              Save Session Artifacts
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostSessionForm;
